import React from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { eur } from './DetailKpis';
import { DEFAULT_CATEGORY_CONFIG } from '../ExpensesPanel';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return m ? `${MONTHS[Number(m) - 1]} ${y.slice(2)}` : ym;
};

export const MonthlyChart = ({ monthly, color, selected, onSelect }) => (
  <div className="bg-white rounded-xl border shadow-sm p-4 h-full" data-testid="expense-detail-monthly-chart">
    <div className="flex items-center justify-between mb-3 gap-2">
      <h3 className="font-semibold text-gray-900">Evolución mensual</h3>
      <span className="text-xs text-gray-400">Pulsa un mes para ver sus facturas</span>
    </div>
    {monthly.length === 0 ? (
      <p className="text-sm text-gray-400 py-10 text-center">Sin datos en este periodo</p>
    ) : (
      <>
        <div className="h-48 [&_*:focus]:outline-none">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly.map(m => ({ ...m, label: monthLabel(m.month) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={50} />
              <Tooltip formatter={(v) => eur(v)} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]} className="cursor-pointer" onClick={(d) => onSelect?.(d.month)}>
                {monthly.map(m => <Cell key={m.month} fill={color} fillOpacity={!selected || selected === m.month ? 1 : 0.25} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="expense-detail-month-buttons">
          {monthly.map(m => (
            <button key={m.month} onClick={() => onSelect?.(m.month)} data-testid={`expense-detail-month-btn-${m.month}`}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selected === m.month ? 'text-white border-transparent' : 'text-gray-700 hover:bg-gray-50'}`}
              style={selected === m.month ? { backgroundColor: color } : undefined}>
              {monthLabel(m.month)} · {eur(m.total)}
            </button>
          ))}
        </div>
      </>
    )}
  </div>
);

export const VendorRanking = ({ vendors, color, onSelect, selected }) => {
  const max = vendors[0]?.total || 1;
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 h-full" data-testid="expense-detail-vendor-ranking">
      <h3 className="font-semibold text-gray-900 mb-3">Ranking de proveedores</h3>
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {vendors.length === 0 && <p className="text-sm text-gray-400 py-10 text-center">Sin proveedores</p>}
        {vendors.map((v, i) => (
          <button key={v.vendor} onClick={() => onSelect(v.vendor === 'Sin proveedor' ? '' : v.vendor)}
            className={`w-full text-left p-2 rounded-lg transition-colors ${selected === v.vendor ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
            data-testid={`expense-detail-vendor-${i}`}>
            <div className="flex justify-between text-sm">
              <span className="font-medium truncate">{i + 1}. {v.vendor}</span>
              <span className="font-semibold shrink-0 ml-2">{eur(v.total)}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                <div className="h-1.5 rounded-full" style={{ width: `${(v.total / max) * 100}%`, backgroundColor: color }} />
              </div>
              <span className={`text-xs ${selected === v.vendor ? 'text-gray-300' : 'text-gray-400'}`}>{v.count} fact.</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const CategoryBreakdown = ({ byCategory }) => {
  const entries = Object.entries(byCategory);
  const max = entries[0]?.[1] || 1;
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 h-full" data-testid="expense-detail-category-breakdown">
      <h3 className="font-semibold text-gray-900 mb-3">Por categoría</h3>
      {entries.length === 0 && <p className="text-sm text-gray-400 py-10 text-center">Sin datos</p>}
      <div className="space-y-2">
        {entries.map(([cat, total]) => {
          const cfg = DEFAULT_CATEGORY_CONFIG[cat] || { label: cat, color: '#6B7280' };
          return (
            <div key={cat}>
              <div className="flex justify-between text-sm"><span className="font-medium">{cfg.label}</span><span className="font-semibold">{eur(total)}</span></div>
              <div className="h-1.5 bg-gray-200 rounded-full mt-1"><div className="h-1.5 rounded-full" style={{ width: `${(total / max) * 100}%`, backgroundColor: cfg.color }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
